// External libraries
import { Grid } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

// Custom components
import { HSpacer, RoutalDialog, RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { CrossIconStyled } from '../../../../dialogs/DialogCloseIcon';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import ImportStopsDialogMapper, { IMapField } from './ImportDialog/ImportStopsDialogMapper';
import { ImportStopsDialogReview } from './ImportDialog/ImportStopsDialogReview';
import ImportStopsDialogUpload from './ImportDialog/ImportStopsDialogUpload';

// Domain
import { CustomFieldDefinition, CustomFieldsDTO, CustomFieldsMapper, Location } from '@nx-smartmonkey/shared/domain';
import { Stop } from '../../../../domain/stop/Stop';
import { Task } from '../../../../domain/task/Task';

// Helpers
import { TimeWindows } from '@nx-smartmonkey/shared/interfaces';
import { PLAN_TYPE } from '../../../../constants/Subscriptions';
import { intl } from '../../../../helpers/IntGlobalProvider';
import {
  parseAddresses,
  parseBoolean,
  parseCategorical,
  parseList,
  parseMinutes,
  parseNumber,
  parsePhone,
  parseString,
  parseTimeWindows,
} from '../../../../helpers/excel/Parsers';
import { useGetMapDefaults } from '../../../../hooks/map/useGetMapDefaults';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { useCreateStops } from '../../../../hooks/stop/useCreateStops';
import { useRetrieveAllSupervisors } from '../../../../hooks/supervisor/useRetrieveAllSupervisors';
import eventsPanel from '../../../../services/events';

// Assets
import UploadExcel from '../../../../assets/img/excel.svg';
import InfoIcon from '../../../../assets/img/info-icon.svg';
import { exportToExcel, getStopExamples } from '../../../../assets/templates/examples';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';

const StyledPlaceholder = styled(Grid)`
  height: calc(100% - 32px);
  width: calc(100% - 28px) !important;
  cursor: pointer;
  margin: 16px 14px;
  background-color: ${RoutalPalette.neutral00};
  border-radius: 12px;
`;

interface StopsFromFileDialogProps {
  open: boolean;
  file?: File;
  handleCloseDialog: (...args: any[]) => any;
  onSelectNewVersion: (e: any) => void;
}
interface IData {
  headers?: string[];
  body?: string[][];
}

const viewToServiceConstraints: { [key: string]: string } = {
  provide_requires: `requires`,
  cluster: `cluster`,
  reward: `reward`,
  time_windows: `time_windows`,
  volume: `volume`,
  weight: `weight`,
  pickups: `pickups`,
};

const stopValidators: IStopSchema<(value: any) => Promise<any> | any> = {
  label: parseString,
  location: parseString,
  duration: parseMinutes,
  requires: parseList,
  cluster: parseString,
  reward: parseNumber,
  time_windows: parseTimeWindows,
  optional: parseBoolean,
  weight: parseNumber,
  volume: parseNumber,
  max_services: parseNumber,
  comments: parseString,
  phone: parsePhone,
  email: parseString,
  url: parseString,
  reference_person: parseString,
  external_id: parseString,
  location_details: parseString,
  supervisors: parseList,
};

const taskValidators: ITaskSchema<(value: any) => Promise<any>> = {
  label: parseString,
  barcode: parseString,
  comments: parseString,
};

const customFieldValidator = {
  text: parseString,
  numerical: parseNumber,
  boolean: parseBoolean,
  categorical: parseCategorical,
};

interface IStopBaseSchema<T> {
  label?: T;
  location?: T;
  duration?: T;
  max_services?: T;
  comments?: T;
  phone?: T;
  email?: T;
  url?: T;
  reference_person?: T;
  external_id?: T;
  location_details?: T;
  supervisors?: T;
}
interface IStopBaseCapacities<T> {
  weight?: T;
  volume?: T;
  requires?: T;
  cluster?: T;
  reward?: T;
  time_windows?: T;
  optional?: T;
}

interface ICustomFields<T> {
  custom_fields?: { [fieldId: string]: T | undefined };
}

interface IStopSchema<T> extends IStopBaseSchema<T>, IStopBaseCapacities<T>, ICustomFields<T> {}
interface IPickupSchema<T> extends IStopBaseSchema<T>, ICustomFields<T> {}
interface IUploadStopSchema<T> {
  stop?: IStopSchema<T>;
  pickup?: IPickupSchema<T>;
  task?: ITaskSchema<T>;
}

interface ITaskBaseSchema<T> {
  label?: T;
  barcode?: T;
  comments?: T;
}

interface ITaskSchema<T> extends ITaskBaseSchema<T>, ICustomFields<T> {}

interface IValidatorResult {
  data?: any[];
  errors: number;
  blank: number;
  correct: number;
}

const validateResults = async (
  headers: string[],
  body: string[][],
  columnName: string | undefined,
  validator: (key: any, ...args: any[]) => Promise<any>,
  ...args: any[]
): Promise<IValidatorResult | undefined> => {
  if (columnName === undefined) {
    return undefined;
  }
  const columnIdx = headers?.indexOf(columnName) ?? -1;

  if (columnIdx === -1) {
    return { blank: 0, correct: 0, errors: 0 };
  }

  if (!validator || !body) {
    return { blank: 0, correct: 0, errors: 0 };
  }

  const responses = await Promise.all(
    body.map((row) => {
      const value = row[columnIdx];
      return validator(value, ...args);
    })
  );

  const validationResults = {
    data: responses,
    correct: responses.filter((response) => response !== null && response !== undefined).length,
    errors: responses.filter((response) => response === null).length,
    blank: responses.filter((response) => response === undefined).length,
  };
  return validationResults;
};

export const StopsFromFileDialog = ({
  open,
  file,
  handleCloseDialog,
  onSelectNewVersion,
}: StopsFromFileDialogProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { getCoordinates } = useGetMapDefaults();
  const { data: supervisors } = useRetrieveAllSupervisors();

  // Declare steps
  const Steps = {
    UPLOAD_DATA: 0,
    MAP_DATA: 1,
    REVIEW_DATA: 2,
  };

  interface StopData {
    duration?: number;
    requires?: string[];
    cluster?: string;
    reward?: number;
    time_windows?: TimeWindows;
    volume?: number;
    weight?: number;
    client_id?: string;
    external_id?: string;
    client_external_id?: string;
    reference_person?: string;
    location?: Location;
    location_details?: string;
    comments?: string;
    phone?: string;
    email?: string;
    url?: string;
    custom_fields?: object;
    task_name?: string;
    task_comments?: string;
    task_barcode?: string;
    supervisors?: string[];
  }

  interface StopTaskData {
    label?: string;
    comments?: string;
    barcode?: string;
    custom_fields?: object;
  }

  interface FileServiceData extends StopData {
    label?: string;
    pickup?: StopData & { label?: string };
    task?: StopTaskData;
    tasks?: StopTaskData[];
  }

  const [step, setStep] = useState(0);
  const [data, setData] = useState({} as IData);
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { run: createStops } = useCreateStops();
  // const [error, setError] = useState<string | undefined>();
  const [isLoading, setLoading] = useState(false);
  const [enabledFields, setEnabledFields] = useState<IUploadStopSchema<boolean>>({});
  const [columnMapping, setColumnMapping] = useState<IUploadStopSchema<string>>({});
  const [customFields, setCustomFields] = useState<{ [id: string]: CustomFieldDefinition }>({});
  const [taskCustomFields, setTaskCustomFields] = useState<{ [id: string]: CustomFieldDefinition }>({});
  const [columnValidation, setColumnValidation] = useState<IUploadStopSchema<IValidatorResult>>({});
  const [newStops, setNewStops] = useState<FileServiceData[]>([]);

  const initMapping = useCallback(
    async (
      headers: string[],
      body: string[][],
      activeFields: IUploadStopSchema<boolean>
    ): Promise<{
      mapping: IUploadStopSchema<string>;
      validation: IUploadStopSchema<IValidatorResult>;
    }> => {
      const mapping: IUploadStopSchema<string> = {};
      const validation: IUploadStopSchema<IValidatorResult> = {};
      try {
        const storedMapping: IUploadStopSchema<string> = JSON.parse(
          localStorage.getItem(`SERVICES_COLUMNS_MAP`) as string
        );
        if (storedMapping && storedMapping.stop && typeof storedMapping === typeof {}) {
          const stopsResult = await initGroup(
            activeFields.stop || {},
            headers,
            body,
            storedMapping.stop,
            stopValidators,
            customFields
          );
          mapping.stop = stopsResult.mapping;
          validation.stop = stopsResult.validation;
        }
        if (storedMapping && storedMapping.pickup && typeof storedMapping.pickup === typeof {}) {
          const pickupResult = await initGroup(
            activeFields.pickup || {},
            headers,
            body,
            storedMapping.pickup,
            stopValidators,
            customFields
          );
          mapping.pickup = pickupResult.mapping;
          validation.pickup = pickupResult.validation;
        }
        if (storedMapping && storedMapping.task && typeof storedMapping.task === typeof {}) {
          const taskResult = await initGroup(
            activeFields.task || {},
            headers,
            body,
            storedMapping.task,
            taskValidators,
            taskCustomFields
          );
          mapping.task = taskResult.mapping;
          validation.task = taskResult.validation;
        }
      } catch (err) {
        console.warn(err);
        localStorage.removeItem(`SERVICES_COLUMNS_MAP`);
      }
      return { mapping, validation };
    },
    [customFields, taskCustomFields]
  );

  // const columnMapping = localStorage.get('columnMap');
  const onFileUpload = useCallback(
    async (uploadedFile: File) => {
      setLoading(true);

      try {
        // get existing mapping
        const reader = new FileReader();
        reader.onload = async (event: ProgressEvent<FileReader>) => {
          const fileData = event?.target?.result;
          if (fileData) {
            const workbook = XLSX.read(fileData, { type: `binary` });

            // Only reading the first sheet
            const workSheetNames = Object.keys(workbook.Sheets);
            if (workSheetNames.length > 0) {
              // eslint-disable-next-line dot-notation
              const workSheet = workbook.Sheets[workSheetNames[0]];

              // Getting the excel column header names
              const headers: string[] = [];
              Object.keys(workSheet).forEach((key: string) => {
                if (/^[A-Z]*1{0,1}$/.test(key)) {
                  headers.push(workSheet[key].v);
                }
              });

              const parsedBody = XLSX.utils.sheet_to_json(workSheet);

              const body = parsedBody.map((row: any) => {
                return headers.map((key: string) => row[key] ?? null);
              });

              // TODO: Validate the input data (no empty headers, no empty body...)
              setData({ headers, body });

              const result = await initMapping(headers, body, enabledFields);
              setColumnMapping(result.mapping);
              setColumnValidation(result.validation);

              setStep(Steps.MAP_DATA);
            }
          }
          setLoading(false);
        };
        reader.readAsBinaryString(uploadedFile);
      } catch (err) {
        console.warn(`ERROR`, err);

        showSnackbarMessage({ messageIntlId: `infoerror.invalid_excel_format` });

        handleClose({ stopPropagation: () => {} });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Steps.MAP_DATA, enabledFields, initMapping]
  );

  useEffect(() => {
    if (file) {
      onFileUpload(file);
    }
  }, [file, onFileUpload]);

  /**
   * Filter fields by those enabled
   */
  useEffect(() => {
    // Filtering only the true values (TASK)
    const taskInfoFields = Object.entries(activeProject?.view?.task.getValue() ?? {})
      .filter((task) => task[1])
      .map((task) => task[0]);

    // Convert them to stop constraints
    const taskTmpCustomFields = activeProject?.custom_fields?.task
      .filter((value) => value.enabled)
      .reduce(
        (prev, curr) => ({
          ...prev,
          [curr.custom_id]: curr,
        }),
        {}
      ) as { [id: string]: CustomFieldDefinition };

    setTaskCustomFields(taskTmpCustomFields || {});
    const reducedTaskCustomFields = taskTmpCustomFields
      ? Object.entries(taskTmpCustomFields).reduce((prev, curr) => ({ ...prev, [curr[1].custom_id]: true }), {})
      : undefined;

    // Filtering only the true values (CLIENT)
    const infoFields = Object.entries(activeProject?.view?.client.getValue() ?? {})
      .filter((value) => value[1] && value[0] !== `optional`)
      .map((value) => value[0]);

    const tmpConstraintFields = Object.entries(activeProject?.view?.constraints.getValue() ?? {})
      .filter((value) => value[1])
      .map((value) => viewToServiceConstraints[value[0]])
      .filter((val) => val);

    // Convert them to stop constraints
    const tmpCustomFields = activeProject?.custom_fields?.client
      .filter((value) => value.enabled)
      .reduce(
        (prev, curr) => ({
          ...prev,
          [curr.custom_id]: curr,
        }),
        {}
      ) as { [id: string]: CustomFieldDefinition };
    setCustomFields(tmpCustomFields || {});
    const reducedCustomFields = tmpCustomFields
      ? Object.entries(tmpCustomFields).reduce((prev, curr) => ({ ...prev, [curr[1].custom_id]: true }), {})
      : undefined;

    // Remove pickup constraint, we will need to map a few fields instead of just one
    const pickupsEnabled = tmpConstraintFields.indexOf(`pickups`) > -1;
    const filteredConstraintFields = tmpConstraintFields.filter((field) => field !== `pickups`);
    const filteredConstraintFieldsForPickups = filteredConstraintFields.filter(
      (field) => field !== `weight` && field !== `volume`
    );

    const reducedFilteredConstraintFields = filteredConstraintFields.reduce(
      (prev, curr) => ({ ...prev, [curr]: true }),
      {}
    );
    const reducedFilteredConstraintFieldsForPickups = filteredConstraintFieldsForPickups.reduce(
      (prev, curr) => ({ ...prev, [curr]: true }),
      {}
    );

    const reducedInfoFields = infoFields.reduce((prev, curr) => ({ ...prev, [curr]: true }), {});
    const reducedTaskInfoFields = taskInfoFields.reduce((prev, curr) => ({ ...prev, [curr]: true }), {});

    setEnabledFields({
      stop: {
        label: true,
        location: true,
        duration: true,
        ...reducedInfoFields,
        ...reducedFilteredConstraintFields,
        ...(tmpCustomFields
          ? {
              custom_fields: { ...reducedCustomFields },
            }
          : {}),
        supervisors: organization?.subscription.plan !== PLAN_TYPE.MONKEY,
      },
      ...(pickupsEnabled
        ? {
            pickup: {
              label: true,
              location: true,
              duration: true,
              ...reducedInfoFields,
              ...reducedFilteredConstraintFieldsForPickups,
              ...(tmpCustomFields
                ? {
                    custom_fields: { ...reducedCustomFields },
                  }
                : {}),
              supervisors: organization?.subscription.plan !== PLAN_TYPE.MONKEY,
            },
          }
        : {}),
      ...(activeProject?.tasks_enabled
        ? {
            task: {
              label: true,
              ...reducedTaskInfoFields,
              ...(taskTmpCustomFields
                ? {
                    custom_fields: { ...reducedTaskCustomFields },
                  }
                : {}),
            },
          }
        : {}),
    });
  }, [activeProject, organization]);

  const initGroup = async (
    activeFields: IStopSchema<boolean> | IPickupSchema<boolean> | ITaskSchema<boolean>,
    headers: string[],
    body: string[][],
    storedMapping: IStopSchema<string> | IPickupSchema<string> | ITaskSchema<string>,
    validators: IStopSchema<(value: any) => Promise<any>> | ITaskSchema<(value: any) => Promise<any>>,
    customFields: { [id: string]: CustomFieldDefinition }
  ) => {
    const result = {
      validation: {} as any,
      mapping: {} as any,
    };

    if (Object.keys(storedMapping).every((value) => typeof value === typeof `abc`)) {
      for await (const entry of Object.entries(storedMapping)) {
        if (entry[0] === `custom_fields`) {
          for await (const customFieldEntry of Object.entries(entry[1] as ICustomFields<string>)) {
            if (
              Object.keys(activeFields.custom_fields || {}).indexOf(customFieldEntry[0]) > -1 &&
              headers.indexOf(customFieldEntry[1]) > -1 &&
              customFields[customFieldEntry[0]]
            ) {
              const projectCustomField = customFields[customFieldEntry[0]]!;

              result.mapping = {
                ...result.mapping,
                custom_fields: {
                  ...result.mapping.custom_fields,
                  [customFieldEntry[0]]: customFieldEntry[1],
                },
              };

              const validator = customFieldValidator[projectCustomField.field_type];
              const promise = await validateResults(
                headers,
                body,
                customFieldEntry[1],
                validator,
                ...(customFields[customFieldEntry[0]] ? [customFields[customFieldEntry[0]]] : [undefined])
              );
              result.validation = {
                ...result.validation,
                custom_fields: {
                  ...result.validation.custom_fields,
                  [customFieldEntry[0]]: promise,
                },
              };
            }
          }
        } else if (Object.keys(activeFields).indexOf(entry[0]) > -1 && headers.indexOf(entry[1]) > -1) {
          result.mapping[entry[0]] = entry[1];
          const promise = await validateResults(headers, body, entry[1], (validators as any)[entry[0]]);
          result.validation[entry[0]] = promise;
        }
      }
    }
    return result;
  };

  const onColumnMapped = async (field: IMapField, newValue: string | undefined) => {
    let newMapping;
    let newValidation;
    let validation;
    if (field.category === `delivery.custom_fields` || field.category === `pickup.custom_fields`) {
      const validator = customFieldValidator[customFields[field.key].field_type];
      validation = await validateResults(
        data.headers || [],
        data.body || [],
        newValue,
        validator,
        ...(customFields[field.key] ? [customFields[field.key]] : [undefined])
      );
      if (field.category === `delivery.custom_fields`) {
        newMapping = {
          ...columnMapping,
          stop: {
            ...columnMapping.stop,
            custom_fields: {
              ...columnMapping.stop?.custom_fields,
              [field.key]: newValue,
            },
          },
        };
        newValidation = {
          ...columnValidation,
          stop: {
            ...columnValidation.stop,
            custom_fields: {
              ...columnValidation.stop?.custom_fields,
              [field.key]: validation,
            },
          },
        };
      } else if (field.category === `pickup.custom_fields`) {
        newMapping = {
          ...columnMapping,
          pickup: {
            ...columnMapping.pickup,
            custom_fields: {
              ...columnMapping.pickup?.custom_fields,
              [field.key]: newValue,
            },
          },
        };
        newValidation = {
          ...columnValidation,
          pickup: {
            ...columnValidation.pickup,
            custom_fields: {
              ...columnValidation.pickup?.custom_fields,
              [field.key]: validation,
            },
          },
        };
      }
    } else if (field.category === `task.custom_fields`) {
      const validator = customFieldValidator[taskCustomFields[field.key].field_type];
      validation = await validateResults(
        data.headers || [],
        data.body || [],
        newValue,
        validator,
        ...(taskCustomFields[field.key] ? [taskCustomFields[field.key]] : [undefined])
      );

      newMapping = {
        ...columnMapping,
        task: {
          ...columnMapping.task,
          custom_fields: {
            ...columnMapping.task?.custom_fields,
            [field.key]: newValue,
          },
        },
      };
      newValidation = {
        ...columnValidation,
        task: {
          ...columnValidation.task,
          custom_fields: {
            ...columnValidation.task?.custom_fields,
            [field.key]: validation,
          },
        },
      };
    } else if (field.category === `delivery` || field.category === `pickup`) {
      validation = await validateResults(
        data.headers || [],
        data.body || [],
        newValue,
        (stopValidators as any)[field.key] || undefined
      );
      if (field.category === `delivery`) {
        newMapping = {
          ...columnMapping,
          stop: { ...columnMapping.stop, [field.key]: newValue },
        };
        newValidation = {
          ...columnValidation,
          stop: { ...columnValidation.stop, [field.key]: validation },
        };
      } else if (field.category === `pickup`) {
        newMapping = {
          ...columnMapping,
          pickup: { ...columnMapping.pickup, [field.key]: newValue },
        };
        newValidation = {
          ...columnValidation,
          pickup: { ...columnValidation.pickup, [field.key]: validation },
        };
      }
    } else if (field.category === `task`) {
      validation = await validateResults(
        data.headers || [],
        data.body || [],
        newValue,
        (taskValidators as any)[field.key] || undefined
      );
      newMapping = {
        ...columnMapping,
        task: { ...columnMapping.task, [field.key]: newValue },
      };
      newValidation = {
        ...columnValidation,
        task: { ...columnValidation.task, [field.key]: validation },
      };
    }
    if (newMapping && newValidation) {
      setColumnMapping(newMapping);
      setColumnValidation(newValidation);
    }

    // console.info(`newMapping`, newMapping);
    console.info(`newValidation`, newValidation);
  };

  const fields = (() => {
    // const hasClientCustomFields = Object.keys(enabledFields.pickup || {}).length > 0;
    const cleanServiceKeys = Object.keys(enabledFields.stop || {}).filter((value) => value !== `custom_fields`);
    const cleanPickupKeys = Object.keys(enabledFields.pickup || {}).filter((value) => value !== `custom_fields`);

    // const hasTaskCustomFields = Object.keys(enabledFields.task || {}).length > 0;
    const cleanTaskKeys = Object.keys(enabledFields.task || {}).filter((value) => value !== `custom_fields`);

    const f = [
      ...cleanServiceKeys.map((fieldId: string) => ({
        key: fieldId,
        ...(fieldId === `phone`
          ? {
              text: (
                <>
                  {intl.formatMessage({ id: `properties.service.${fieldId}` })}
                  <RoutalTooltip
                    title={<Text style={{ color: RoutalPalette.neutral00 }} intlId="properties.mapper.phone_hint" />}
                    overlayStyle={{
                      maxWidth: `300px`,
                    }}
                  >
                    <img style={{ marginLeft: `8px` }} src={InfoIcon} alt="" height="16" width="16" draggable="false" />
                  </RoutalTooltip>
                </>
              ),
            }
          : {
              intlId: `properties.service.${fieldId}`,
            }),
        category: `delivery`,
        value: (columnMapping?.stop || ({} as any))[fieldId],
        validation: (columnValidation?.stop || ({} as any))[fieldId],
      })),
      ...Object.keys(customFields).map((fieldId) => ({
        key: fieldId,
        text: customFields[fieldId].label,
        category: `delivery.custom_fields`,
        value: (columnMapping.stop?.custom_fields || ({} as any))[fieldId],
        validation: (columnValidation?.stop?.custom_fields || ({} as any))[fieldId],
      })),
      ...cleanPickupKeys.map((fieldId) => ({
        key: fieldId,
        intlId: `properties.service.${fieldId}`,
        category: `pickup`,
        value: (columnMapping.pickup || ({} as any))[fieldId],
        validation: (columnValidation?.pickup || ({} as any))[fieldId],
      })),
      ...(enabledFields.pickup
        ? [
            ...Object.keys(customFields).map((fieldId) => ({
              key: fieldId,
              text: customFields[fieldId].label,
              category: `pickup.custom_fields`,
              value: (columnMapping.pickup?.custom_fields || ({} as any))[fieldId],
              validation: (columnValidation?.pickup?.custom_fields || ({} as any))[fieldId],
            })),
          ]
        : []),
      ...cleanTaskKeys.map((fieldId) => ({
        key: fieldId,
        intlId: `properties.stop.tasks.${fieldId}`,
        category: `task`,
        value: (columnMapping.task || ({} as any))[fieldId],
        validation: (columnValidation?.task || ({} as any))[fieldId],
      })),
      ...(enabledFields.task
        ? [
            ...Object.keys(taskCustomFields).map((fieldId) => ({
              key: fieldId,
              text: taskCustomFields[fieldId].label,
              category: `task.custom_fields`,
              value: (columnMapping.task?.custom_fields || ({} as any))[fieldId],
              validation: (columnValidation?.task?.custom_fields || ({} as any))[fieldId],
            })),
          ]
        : []),
    ];
    return f;
  })();

  const onMapFields = async () => {
    // Create N empty stops
    setLoading(true);

    localStorage.setItem(`SERVICES_COLUMNS_MAP`, JSON.stringify(columnMapping));

    const stops: FileServiceData[] = _.range(0, data.body?.length).map(() => ({}));

    // Iterate over all stop fields
    Object.entries(columnValidation.stop || {}).forEach((entry) => {
      const [key, value] = entry;
      if (!value) {
        return;
      }
      // Iterate over all custom fields
      if (key === `custom_fields`) {
        Object.entries((value || {}) as { [key: string]: IValidatorResult }).forEach((customFieldsEntry) => {
          const [customFieldsKey, customFieldsValue] = customFieldsEntry;
          if (!customFieldsValue) {
            return;
          }
          const values = customFieldsValue.data;
          if (values) {
            _.range(0, data.body?.length).forEach((idx) => {
              if (values[idx]) {
                if ((stops[idx] as any).custom_fields) {
                  (stops[idx] as any).custom_fields[customFieldsKey] = values[idx];
                } else {
                  (stops[idx] as any).custom_fields = { [customFieldsKey]: values[idx] };
                }
              }
            });
          }
        });
      } else {
        const values = value.data;
        _.range(0, data.body?.length).forEach((idx) => {
          if (values[idx]) {
            (stops[idx] as any)[key] = values[idx];
          }
        });
      }
    });

    // Iterate over all pickup fields
    Object.entries(columnValidation.pickup || {}).forEach(async (entry) => {
      const [key, value] = entry;
      if (!value) {
        return;
      }

      if (key === `custom_fields`) {
        Object.entries((value || {}) as { [key: string]: IValidatorResult }).forEach((customFieldsEntry) => {
          const [customFieldsKey, customFieldsValue] = customFieldsEntry;
          if (!customFieldsValue) {
            return;
          }
          const values = customFieldsValue.data;
          if (values) {
            _.range(0, data.body?.length).forEach((idx) => {
              if (values[idx]) {
                if ((stops[idx] as any).pickup) {
                  if ((stops[idx] as any).pickup.custom_fields) {
                    (stops[idx] as any).pickup.custom_fields[customFieldsKey] = values[idx];
                  } else {
                    (stops[idx] as any).pickup.custom_fields = { [customFieldsKey]: values[idx] };
                  }
                } else {
                  (stops[idx] as any).pickup = { custom_fields: { [customFieldsKey]: values[idx] } };
                }
              }
            });
          }
        });
      } else {
        const values = value.data;
        _.range(0, data.body?.length).forEach((idx) => {
          if (values[idx])
            if (stops[idx].pickup) {
              (stops[idx] as any).pickup[key] = values[idx];
            } else {
              (stops[idx] as any).pickup = { [key]: values[idx] };
            }
        });
      }
    });

    // Iterate over all task fields
    Object.entries(columnValidation.task || {}).forEach(async (entry) => {
      const [key, value] = entry;
      if (!value) {
        return;
      }

      if (key === `custom_fields`) {
        Object.entries((value || {}) as { [key: string]: IValidatorResult }).forEach((customFieldsEntry) => {
          const [customFieldsKey, customFieldsValue] = customFieldsEntry;
          if (!customFieldsValue) {
            return;
          }
          const values = customFieldsValue.data;
          if (values) {
            _.range(0, data.body?.length).forEach((idx) => {
              if (values[idx]) {
                if ((stops[idx] as any).task) {
                  if ((stops[idx] as any).task.custom_fields) {
                    (stops[idx] as any).task.custom_fields[customFieldsKey] = values[idx];
                  } else {
                    (stops[idx] as any).task.custom_fields = { [customFieldsKey]: values[idx] };
                  }
                } else {
                  (stops[idx] as any).task = { custom_fields: { [customFieldsKey]: values[idx] } };
                }
              }
            });
          }
        });
      } else {
        const values = value.data;
        _.range(0, data.body?.length).forEach((idx) => {
          if (values[idx])
            if (stops[idx].task) {
              (stops[idx] as any).task[key] = values[idx];
            } else {
              (stops[idx] as any).task = { [key]: values[idx] };
            }
        });
      }
    });

    try {
      // Geocode stops
      if (columnValidation.stop?.location?.data) {
        const stopAddresses = await parseAddresses({
          addresses: columnValidation.stop?.location?.data ?? [],
          projectId: activeProject?.id,
        });
        stopAddresses.forEach((address: any, idx: number) => {
          stops[idx].location = address ?? undefined;
        });
      }
    } catch (error) {
      console.warn(`Error while doing the stops geocode`);
      showSnackbarMessage({
        autoHideDuration: 5000,
        ...(error === `highway.maps.error.not_enough_credits`
          ? {
              messageIntlId: `infoerror.${error}`,
            }
          : {}),
      });
      setLoading(false);
      setStep(Steps.MAP_DATA);
      return;
    }

    try {
      // Geocode pickups
      if (columnValidation.pickup?.location?.data) {
        const pickupAddresses = await parseAddresses({
          addresses: columnValidation.pickup?.location?.data ?? [],
          projectId: activeProject?.id,
        });
        pickupAddresses.forEach((location: any, idx: number) => {
          const pickup = stops[idx].pickup;
          if (pickup) {
            pickup.location = location ?? undefined;
          } else {
            stops[idx].pickup = { location: location ?? undefined };
          }
        });
      }
    } catch (error) {
      console.warn(`Error while doing the pickups geocode`);
      showSnackbarMessage({
        autoHideDuration: 5000,
        ...(error === `highway.maps.error.not_enough_credits`
          ? {
              messageIntlId: `infoerror.${error}`,
            }
          : {}),
      });
      setLoading(false);
      setStep(Steps.MAP_DATA);
      return;
    }

    if (columnValidation.stop?.supervisors?.data) {
      columnValidation.stop?.supervisors?.data.forEach((supervisorShortIds: string[] | undefined, idx) => {
        const supervisorsFound = (supervisors ?? []).filter((supervisor) =>
          supervisorShortIds?.includes(supervisor.short_id)
        );
        stops[idx].supervisors = supervisorsFound?.map((supervisor) => supervisor.id);
      });
    }

    if (columnValidation.pickup?.supervisors?.data) {
      columnValidation.pickup?.supervisors?.data.forEach((supervisorShortIds: string[] | undefined, idx) => {
        const supervisorsFound = (supervisors ?? []).filter((supervisor) =>
          supervisorShortIds?.includes(supervisor.short_id)
        );

        const pickup = stops[idx].pickup;
        if (pickup) {
          pickup.supervisors = supervisorsFound.map((supervisor) => supervisor.id);
        } else {
          stops[idx].pickup = { supervisors: supervisorsFound.map((supervisor) => supervisor.id) };
        }
      });
    }

    // Merge tasks
    const taskIdsMap: { [key: string]: number } = {};
    const mergedStops = stops.reduce((acc, stop, idx) => {
      if (!columnValidation.stop?.external_id || !stop.external_id || !stop.task) {
        return [...acc, stop];
      }
      if (!(stop.external_id in taskIdsMap) && stop.task) {
        taskIdsMap[stop.external_id] = acc.length;
        const cleanService = {
          ...stop,
          tasks: [stop.task],
        };
        delete cleanService.task;
        return [...acc, cleanService];
      } else {
        const cleanService = acc[taskIdsMap[stop.external_id]];
        cleanService.tasks!.push(stop.task);

        // merge capacities and duration
        if (stop.volume) {
          cleanService.volume = (cleanService.volume || 0) + stop.volume;
        }
        if (stop.weight) {
          cleanService.weight = (cleanService.weight || 0) + stop.weight;
        }
        if (stop.duration) {
          cleanService.duration = (cleanService.duration || 0) + stop.duration;
        }

        return acc;
      }
    }, [] as FileServiceData[]);

    setNewStops(mergedStops);
    setLoading(false);
    setStep(Steps.REVIEW_DATA);
  };

  const canCreateStops = () => newStops.length > 0;

  const handleCreateStops = (e: any) => {
    const stops: Stop[] = newStops.map((stop) => {
      const { custom_fields: stopCustomFields, task, tasks, pickup, supervisors, ...stopData } = stop;

      let stopPickup: Stop | undefined;
      if (pickup) {
        const { custom_fields: pickupCustomFields, supervisors: pickupSupervisors, ...pickupData } = pickup;

        stopPickup = Stop.create({
          ...pickupData,
          type: `pickup`,
          project_id: activeProject!.id,
          status: `pending`,
          ...(activeProject?.custom_fields && pickupCustomFields
            ? {
                custom_fields: CustomFieldsMapper.toDomain(
                  pickupCustomFields as CustomFieldsDTO,
                  activeProject!.custom_fields!.client
                ),
              }
            : {}),
          supervisors: pickupSupervisors ?? [],
        });
      }

      let stopTasks: Task[] | undefined;
      if ((tasks ?? []).length > 0) {
        stopTasks = tasks!.map((task) => {
          const { custom_fields: taskCustomFields, ...taskData } = task;
          const newTask = Task.create({
            ...taskData,
            status: `pending`,
            stop_id: `fake_stop_id`, // WARNING: stop_id is mandatory but when creating a task we do not have a stop id
            ...(activeProject?.custom_fields && taskCustomFields
              ? {
                  custom_fields: CustomFieldsMapper.toDomain(
                    taskCustomFields as CustomFieldsDTO,
                    activeProject!.custom_fields!.task
                  ),
                }
              : {}),
          });
          return newTask;
        });
      }

      const newStop = Stop.create({
        ...stopData,
        type: `delivery`,
        project_id: activeProject!.id,
        status: `pending`,
        ...(activeProject?.custom_fields && stopCustomFields
          ? {
              custom_fields: CustomFieldsMapper.toDomain(
                stopCustomFields as CustomFieldsDTO,
                activeProject!.custom_fields!.client
              ),
            }
          : {}),
        ...(stopPickup ? { pickup: stopPickup } : {}),
        ...(stopTasks ? { tasks: stopTasks } : {}),
        supervisors: supervisors ?? [],
      });

      return newStop;
    });

    createStops({ stops });
    eventsPanel.createStops(`Subir V1`);
    handleClose(e);
  };

  const onResetDialog = () => {
    switch (step) {
      case Steps.MAP_DATA:
        emptyDialogData();
        break;
      case Steps.REVIEW_DATA:
        setStep(Steps.MAP_DATA);
        break;
      default:
        break;
    }
  };

  const emptyDialogData = () => {
    setData({} as IData);
    // setError(undefined);
    setColumnMapping({});
    setColumnValidation({});

    setStep(Steps.UPLOAD_DATA);
    setLoading(false);
  };

  const handleClose = (e: any) => {
    emptyDialogData();
    handleCloseDialog(e);
  };

  const getNumberOfStops = () => {
    const numOfDeliveries = newStops.length;
    const numOfPickups = newStops.filter((stop: FileServiceData) => !!stop.pickup).length;
    return numOfDeliveries + numOfPickups;
  };

  const getTitleIntlId = () => {
    switch (step) {
      case Steps.MAP_DATA:
        return `plans.dialogs.services_from_file.map.title`;
      case Steps.REVIEW_DATA:
        return `plans.dialogs.services_from_file.review.title`;
      case Steps.UPLOAD_DATA:
      default:
        return `plans.dialogs.services_from_file.upload.title`;
    }
  };

  return (
    <RoutalDialog
      open={open}
      dialogProps={{
        maxWidth: `md`,
        dialogBGColor: RoutalPalette.neutral00,
      }}
      onCloseDialog={handleClose}
      showCrossCloseButton={false}
      loading={isLoading}
      titleRightContent={
        <Grid item container justifyContent="flex-end" alignItems="center" wrap="nowrap">
          {step === Steps.MAP_DATA ? (
            <>
              <Button variant="text" disabled={isLoading} onClick={onResetDialog} intlId="previous" />
              <HSpacer small />
              <Button
                disabled={isLoading || fields.filter((field) => field.value).length === 0}
                onClick={onMapFields}
                intlId="next"
              />
            </>
          ) : null}

          {step === Steps.REVIEW_DATA ? (
            <>
              <Button variant="text" disabled={isLoading} onClick={onResetDialog} intlId="previous" />
              <HSpacer small />
              <Button
                style={{ minWidth: `120px` }}
                onClick={handleCreateStops}
                intlId={getNumberOfStops() > 1 ? `create_number` : `create`}
                intlValues={getNumberOfStops() > 1 ? { number: getNumberOfStops() } : {}}
                disabled={!canCreateStops()}
              />
            </>
          ) : null}

          <CrossIconStyled onClick={handleClose} />
        </Grid>
      }
      titleContent={<Text variant="h6" intlId={getTitleIntlId()} />}
      contentStyles={{
        height: `400px`,
      }}
      showFooter={false}
    >
      {activeProject && (
        <Grid container style={{ height: `100%`, width: `100%` }}>
          {step === Steps.UPLOAD_DATA && (
            <StyledPlaceholder container direction="column" wrap="nowrap" justifyContent="center">
              <ImportStopsDialogUpload
                onFileUpload={onFileUpload}
                uploadButtonProps={{
                  id: `upload_stops`,
                  intlId: `plans.plan.data.overview.services.empty_placeholder.button`,
                }}
                dropzoneStyles={{
                  display: `flex`,
                  flexDirection: `column`,
                  justifyContent: `center`,
                  alignItems: `center`,
                }}
                afterUploadButtonContent={
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    style={{ height: `40px`, marginTop: `40px` }}
                  >
                    <Text
                      variant="button"
                      intlId="plans.dialogs.upload_stops.try_new_version"
                      onClick={onSelectNewVersion}
                    />
                  </Grid>
                }
              >
                <Grid item container justifyContent="center">
                  <img alt="Upload xlsx" src={UploadExcel} width="80" />
                </Grid>

                <VSpacer small />

                <Grid item container direction="column" alignItems="center">
                  <Text
                    variant="h6"
                    style={{ cursor: `pointer`, color: RoutalPalette.secondary60 }}
                    intlId="plans.plan.data.overview.services.empty_placeholder.title"
                  />

                  <VSpacer small />

                  <Button
                    variant="flat"
                    styleType="secondary"
                    intlId="plans.plan.data.overview.services.empty_placeholder.download_template"
                    onClick={() => {
                      const coordinates = getCoordinates();
                      if (coordinates) {
                        exportToExcel(getStopExamples(coordinates, activeProject), `stops_example`);
                      }
                    }}
                  />
                </Grid>
              </ImportStopsDialogUpload>
            </StyledPlaceholder>
          )}
          {step === Steps.MAP_DATA && (
            <ImportStopsDialogMapper headers={data?.headers ?? []} fields={fields} onChange={onColumnMapped} />
          )}
          {step === Steps.REVIEW_DATA && (
            <ImportStopsDialogReview
              stops={newStops}
              onAddressChange={({
                stopIndex,
                newAddress,
                isPickup,
              }: {
                stopIndex: number;
                newAddress?: Location;
                isPickup?: boolean;
              }) => {
                const copy = [...newStops];
                if (!isPickup) {
                  copy[stopIndex].location = newAddress;
                } else {
                  const pickup = copy[stopIndex].pickup;
                  if (pickup) {
                    pickup.location = newAddress;
                  }
                }
                setNewStops(copy);
              }}
            />
          )}
        </Grid>
      )}
    </RoutalDialog>
  );
};
